import { useCallback, useMemo } from 'react';

import { type ApolloQueryResult, type OperationVariables, useMutation, useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';

import { tournamentIdAtom } from 'atoms/tournament';
import { type InitialDrawDisplaySettings } from 'components/dialogs/draw/publicDrawDisplaySettings/types';
import { GET_DRAW_SETTINGS, UPDATE_DRAW_SETTINGS } from 'config/graphql/queries';
import { isStandalone } from 'utils/environment';

export enum VirtualCheckInType {
  TOURNAMENT = 'TOURNAMENT_SIGN_IN',
  MATCH = 'MATCH_CHECK_IN',
}

export interface VirtualCheckInService {
  virtualTournamentCheckIn?: boolean;
  virtualMatchUpCheckIn?: boolean;
  updateVirtualCheckIn: (checkInType: VirtualCheckInType) => Promise<void>;
  refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<InitialDrawDisplaySettings>>;
}

const useVirtualCheckIn = (): VirtualCheckInService => {
  const tournamentId = useAtomValue(tournamentIdAtom);
  const [update] = useMutation(UPDATE_DRAW_SETTINGS);

  const { data, refetch } = useQuery<InitialDrawDisplaySettings>(GET_DRAW_SETTINGS, {
    fetchPolicy: isStandalone() ? 'cache-only' : 'cache-first',
    variables: { tournamentId },
  });

  const { virtualTournamentCheckIn, virtualMatchUpCheckIn } = useMemo(
    () =>
      !isStandalone()
        ? {
            virtualTournamentCheckIn: data?.settings?.virtualTournamentCheckIn,
            virtualMatchUpCheckIn: data?.settings?.virtualMatchUpCheckIn,
          }
        : {},
    [data?.settings?.virtualTournamentCheckIn, data?.settings?.virtualMatchUpCheckIn],
  );

  const updateVirtualCheckIn = useCallback(
    async (checkInType: VirtualCheckInType) => {
      if (!isStandalone()) {
        const { data: updatedData } = await refetch();

        const updates = {
          variables: {
            tournamentId,
            update: {
              virtualTournamentCheckIn:
                checkInType === VirtualCheckInType.TOURNAMENT
                  ? !updatedData?.settings?.virtualTournamentCheckIn
                  : updatedData?.settings?.virtualTournamentCheckIn,
              virtualMatchUpCheckIn:
                checkInType === VirtualCheckInType.MATCH
                  ? !updatedData?.settings?.virtualMatchUpCheckIn
                  : updatedData?.settings?.virtualMatchUpCheckIn,
            },
          },
        };

        await update(updates);
        await refetch();
      }
    },
    [tournamentId, update, refetch],
  );

  return { virtualTournamentCheckIn, virtualMatchUpCheckIn, updateVirtualCheckIn, refetch };
};

export default useVirtualCheckIn;
